import { Zap, Tag, FileText, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const features = [
  {
    icon: Zap,
    title: "AI Extraction",
    description: "Captures name, company, title, phone, email, website and address from signatures.",
    accent: "bg-brand-blue",
  },
  {
    icon: Tag,
    title: "Smart Categories",
    description: "Auto-assigns your custom categories like Customer, Partner, Supplier or Lead.",
    accent: "bg-brand-cyan",
  },
  {
    icon: FileText,
    title: "Smart Notes",
    description: "Concise context notes so you remember who the contact is and why they matter.",
    accent: "bg-brand-orange",
  },
  {
    icon: Coins,
    title: "Adaptive Credits",
    description: "Pay only for what you use. No mandatory subscriptions. Top-ups any time.",
    accent: "bg-brand-pink",
  },
];

const FluentTile = ({ feature, index }: { feature: typeof features[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    delay: index * 80,
    threshold: 0.15,
  });

  return (
    <div ref={ref} className={`relative fluent-tile fluent-tile-hover p-5 md:p-6 bg-card overflow-hidden ${animationClass}`}>
      {/* Top accent bar */}
      <div className={`absolute inset-x-0 top-0 h-[3px] ${feature.accent} opacity-80`} />

      <div className="w-10 h-10 rounded-lg bg-surface-2 border border-border/60 grid place-items-center mb-4">
        <feature.icon className="w-5 h-5 text-foreground" strokeWidth={1.75} />
      </div>
      <h3 className="text-base md:text-lg font-display font-semibold mb-2">{feature.title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
    </div>
  );
};

export const Solution = () => {
  const { ref: headerRef, animationClass: headerAnimation } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    threshold: 0.3,
  });

  const { ref: ctaRef, animationClass: ctaAnimation } = useScrollAnimation<HTMLDivElement>({
    type: "scale",
    delay: 200,
    threshold: 0.4,
  });

  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="container px-4">
        <div className="max-w-6xl mx-auto">
          <div ref={headerRef} className={`max-w-3xl mb-12 md:mb-16 ${headerAnimation}`}>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
              What you get
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mt-3 mb-5">
              CRM intelligence — without the CRM.
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              The smart parts of a CRM, right inside Outlook. Automatic contact extraction, intelligent
              tagging and instant research — nothing you don't need.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {features.map((feature, index) => (
              <FluentTile key={index} feature={feature} index={index} />
            ))}
          </div>

          <div ref={ctaRef} className={`mt-10 md:mt-14 ${ctaAnimation}`}>
            <Button size="lg" asChild>
              <Link to="/pricing">View plans</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
